import React, { useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost, downloadBlob } from "../drawings/_drawingsCommon";

const SAMPLE_LINES = [
  {
    id: "L-100-001-1A1", size_in: 6, design_T_C: 250, design_P_barg: 25,
    pipe_class: { id: "3A1", schedule: "STD", rating_class: 300, material: "ASTM A335-P11" },
    material: "ASTM A335-P11",
    "3d_routing": { waypoints: [
      { x: 0, y: 0, z: 1.5 }, { x: 25, y: 0, z: 1.5 },
      { x: 25, y: 8, z: 1.5 }, { x: 25, y: 8, z: 6 },
    ] },
  },
  {
    id: "L-200-005-1A1", size_in: 4, design_T_C: 80, design_P_barg: 5,
    material: "ASTM A106-B",
  },
];

export default function StressScreenerPanel({ projectId }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [adhocLine, setAdhocLine] = useState(JSON.stringify(SAMPLE_LINES[0], null, 2));
  const [mode, setMode] = useState("project");  // "project" | "adhoc"

  const screen = async () => {
    setBusy(true); setErr(""); setResult(null);
    try {
      const body = mode === "adhoc" ? { line: JSON.parse(adhocLine) } : {};
      const { data } = await api.post(`/projects/${projectId}/stress/screen`, body);
      setResult(data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
    } finally { setBusy(false); }
  };

  const downloadPCF = async (lineId, lineObj = null) => {
    setErr("");
    try {
      const body = lineObj ? { line: lineObj } : {};
      const { data } = await api.post(`/projects/${projectId}/stress/${lineId}.pcf`, body, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      downloadBlob(url, `${lineId}.pcf`);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      try {
        const txt = await e?.response?.data?.text?.();
        setErr(formatErr(txt && JSON.parse(txt).detail) || String(e));
      } catch { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    }
  };

  const downloadAllPCF = async (onlyCritical) => {
    setErr("");
    try {
      const { data } = await api.post(`/projects/${projectId}/stress/all.pcf`,
        { only_critical: onlyCritical }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      downloadBlob(url, `stress-${onlyCritical ? "critical" : "all"}-${projectId.slice(0, 8)}.pcf`);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      try {
        const txt = await e?.response?.data?.text?.();
        setErr(formatErr(txt && JSON.parse(txt).detail) || String(e));
      } catch { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    }
  };

  return (
    <div data-testid="stress-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...card, flexDirection: "row", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ ...label, marginBottom: 2 }}>Pipe Stress Screener · PCF Export</div>
          <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
            8-rule screening per ASME B31.3 + Intergraph PCF v9 export — drag-drops directly into Caesar II / AutoPIPE / CAEPIPE / ROHR2.
          </div>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {[["project", "Project"], ["adhoc", "Ad-hoc line"]].map(([k, l]) => (
            <button key={k} data-testid={`stress-mode-${k}`}
              style={mode === k ? btn : btnGhost} onClick={() => setMode(k)}>{l}</button>
          ))}
        </div>
        <button data-testid="stress-screen" style={btn} onClick={screen} disabled={busy}>
          {busy ? "Screening…" : "Screen Lines"}
        </button>
      </div>

      {mode === "adhoc" && (
        <div style={card}>
          <div style={{ ...label, marginBottom: 6 }}>Ad-hoc Line JSON</div>
          <textarea data-testid="stress-adhoc-json"
            style={{ width: "100%", minHeight: 180, padding: 8, fontSize: 11, fontFamily: "monospace",
                     background: "var(--pf-surface-2)", color: "var(--pf-fg)", border: "1px solid var(--pf-border)", borderRadius: 4 }}
            value={adhocLine} onChange={(e) => setAdhocLine(e.target.value)} />
          <div style={{ marginTop: 6, fontSize: 11, color: "var(--pf-muted)" }}>
            Sample line: 6″ class 3A1 sour-service, 250 °C / 25 barg, 4 waypoints.
          </div>
        </div>
      )}

      {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 12 }}>{err}</div>}

      {result && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
            <Kpi label="Total lines" value={result.total_lines} testid="stress-kpi-total" />
            <Kpi label="Critical lines" value={result.critical_lines} color="#ef4444" testid="stress-kpi-critical" />
            <Kpi label="Non-critical" value={result.total_lines - result.critical_lines} color="#22c55e" testid="stress-kpi-noncrit" />
          </div>

          {mode === "project" && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button data-testid="stress-pcf-critical" style={btn} onClick={() => downloadAllPCF(true)}>
                Export Critical Lines as PCF (Caesar II)
              </button>
              <button data-testid="stress-pcf-all" style={btnGhost} onClick={() => downloadAllPCF(false)}>
                Export ALL Lines as PCF
              </button>
            </div>
          )}

          <div style={card}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "var(--pf-surface-2)" }}>
                    {["Line", "T (°C)", "P (barg)", "Size (in)", "Length (m)", "Δexp (mm)", "Status", "Reasons", "Action"].map((h) =>
                      <th key={h} style={{ padding: 6, textAlign: "left", color: "var(--pf-muted)", textTransform: "uppercase", fontSize: 9 }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {result.verdicts.map((v, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid var(--pf-border)" }} data-testid={`stress-row-${i}`}>
                      <td style={{ padding: 6, fontWeight: 700, color: "#F26B1F", fontFamily: "monospace" }}>{v.line_number}</td>
                      <td style={{ padding: 6 }}>{v.design_T_C}</td>
                      <td style={{ padding: 6 }}>{v.design_P_barg}</td>
                      <td style={{ padding: 6 }}>{v.size_inch}</td>
                      <td style={{ padding: 6 }}>{v.length_m}</td>
                      <td style={{ padding: 6 }}>{v.thermal_expansion_mm}</td>
                      <td style={{ padding: 6 }}>
                        <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 3,
                                       background: v.is_critical ? "rgba(239,68,68,.15)" : "rgba(34,197,94,.15)",
                                       color: v.is_critical ? "#ef4444" : "#22c55e", fontWeight: 700 }}>
                          {v.is_critical ? "CRITICAL" : "OK"}
                        </span>
                      </td>
                      <td style={{ padding: 6, fontSize: 10, color: "var(--pf-muted)", maxWidth: 260 }}>
                        {(v.criticality_reasons || []).slice(0, 2).join("; ") || "—"}
                      </td>
                      <td style={{ padding: 6 }}>
                        {v.is_critical && (
                          mode === "adhoc" ? (
                            <button style={{ ...btnGhost, padding: "3px 8px", fontSize: 10 }}
                              onClick={() => downloadPCF(v.line_number, JSON.parse(adhocLine))}>PCF ↓</button>
                          ) : (
                            <button style={{ ...btnGhost, padding: "3px 8px", fontSize: 10 }}
                              onClick={() => downloadPCF(v.line_number)}>PCF ↓</button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                  {result.verdicts.length === 0 && (
                    <tr><td colSpan="9" style={{ padding: 16, textAlign: "center", color: "var(--pf-muted)" }}>No lines screened.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!result && !busy && (
        <div style={{ ...card, textAlign: "center", color: "var(--pf-muted)", padding: 24 }}>
          Click "Screen Lines" to run the 8-rule criticality check on every line in the latest run.
        </div>
      )}
    </div>
  );
}

function Kpi({ label: l, value, color, testid }) {
  return (
    <div data-testid={testid} style={{ padding: 10, background: "var(--pf-surface)", border: "1px solid var(--pf-border)", borderRadius: 6 }}>
      <div style={{ fontSize: 9, color: "var(--pf-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>{l}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || "#F26B1F", marginTop: 2 }}>{value}</div>
    </div>
  );
}
